import {
  BinaryAnswer,
  BinaryAnswerValue,
  BinaryEvaluationQuestion,
  EvaluationCategory,
  EvaluationFragment,
  EvaluationQuestion,
  RatingAnswer,
  RatingAnswerValue,
  RatingEvaluationQuestion,
  ScoreCardBinaryQuestion,
  ScoreCardCategory,
  UpdateEvaluationMutationVariables,
} from "@/graphql/generated/graphql";
import { Unpacked } from "@/helpers/types/types.helpers";
import { CreateEvaluationInput } from "../EvaluationCreateForm/EvaluationCreateForm.decl";
import { EvaluationFieldProps } from "../EvaluationField/EvaluationField.decl";
import { ExcludeNotApplicable } from "../EvaluationResult/EvaluationResult.decl";
import {
  ComputeNextValueArgs,
  NullAnswers,
  UpdateEvaluationOverride,
} from "./EvaluationUpdateForm.decl";

export function isBinaryEvaluationQuestion(
  question: EvaluationQuestion
): question is BinaryEvaluationQuestion {
  return question.__typename === "BinaryEvaluationQuestion";
}
export function isRatingEvaluationQuestion(
  question: EvaluationQuestion
): question is RatingEvaluationQuestion {
  return question.__typename === "RatingEvaluationQuestion";
}

export function hasNotApplicableAnswer(question: EvaluationQuestion) {
  if (isBinaryEvaluationQuestion(question)) {
    return question.binaryAnswer === "NOT_APPLICABLE";
  }

  return question.ratingAnswer === "NOT_APPLICABLE";
}

export function getDescription(question: EvaluationQuestion) {
  if (question.questionAiEnabled) {
    return "";
  }

  if (isBinaryEvaluationQuestion(question)) {
    return (
      question.questionPossibleAnswers.find(
        (answer) => answer.binaryValue === question.binaryAnswer
      )?.answerGuide ?? ""
    );
  }

  if (isRatingEvaluationQuestion(question)) {
    return (
      question.questionPossibleAnswers.find(
        (answer) => answer.ratingValue === question.ratingAnswer
      )?.answerGuide ?? ""
    );
  }

  throw new Error("Unknown question type.");
}

export function getRating(question: EvaluationQuestion) {
  if (isBinaryEvaluationQuestion(question)) {
    return question.binaryAnswer === "NOT_APPLICABLE"
      ? null
      : question.binaryAnswer;
  }

  return question.ratingAnswer === "NOT_APPLICABLE"
    ? null
    : question.ratingAnswer;
}

export function buildBinaryRatingScale(
  answers: BinaryAnswer[]
): EvaluationFieldProps<
  ExcludeNotApplicable<BinaryAnswerValue>
>["ratingScale"] {
  return answers.reduce<
    EvaluationFieldProps<ExcludeNotApplicable<BinaryAnswerValue>>["ratingScale"]
  >(
    (accu, answer) => ({
      ...accu,
      [answer.binaryValue]: answer.points,
    }),
    {
      YES: NaN,
      NO: NaN,
    }
  );
}

export function buildMultipleRatingScale(
  answers: RatingAnswer[]
): EvaluationFieldProps<
  ExcludeNotApplicable<RatingAnswerValue>
>["ratingScale"] {
  return answers.reduce<
    EvaluationFieldProps<ExcludeNotApplicable<RatingAnswerValue>>["ratingScale"]
  >(
    (accu, answer) => ({
      ...accu,
      [answer.ratingValue]: answer.points,
    }),
    {
      VERY_POOR: NaN,
      POOR: NaN,
      FAIR: NaN,
      GOOD: NaN,
      EXCELLENT: NaN,
    }
  );
}

export function initCategories(
  categories: ScoreCardCategory[]
): CreateEvaluationInput["categories"] {
  return categories.map((category) => ({
    categoryName: category.name,
    categoryOrder: category.order,
    scoreCardCategoryId: category.ID,
    questions: category.questions.map((question) => {
      if (question.__typename === "ScoreCardRatingQuestion") {
        return {
          scoreCardQuestionId: question.ID,
          ratingAnswer: null,
          questionPossibleRatingAnswers: question.possibleAnswers.map(
            (answer) => ({
              value: answer.ratingValue,
              points: answer.points,
              answerGuide: answer.answerGuide,
            })
          ),
          questionTotalPoints: question.totalPoints,
          questionText: question.text,
          questionOrder: question.order,
          questionAiEnabled: question.isAIEnabled,
        };
      }

      const questionT = question as ScoreCardBinaryQuestion;
      return {
        scoreCardQuestionId: question.ID,
        binaryAnswer: null,
        questionPossibleBinaryAnswers: questionT.possibleAnswers.map(
          (answer) => ({
            value: answer.binaryValue,
            points: answer.points,
            answerGuide: answer.answerGuide,
          })
        ),
        questionTotalPoints: question.totalPoints,
        questionText: question.text,
        questionOrder: question.order,
        questionAiEnabled: question.isAIEnabled,
      };
    }),
  }));
}

export function computeNextValue({
  currentQuestionValue,
  previousQuestionValue,
}: ComputeNextValueArgs) {
  if (currentQuestionValue === previousQuestionValue) {
    return null;
  }

  return currentQuestionValue;
}

export function initSet(categories: EvaluationFragment["categories"]) {
  if (!categories.length) {
    return;
  }

  return categories.map((category) => category.ID);
}

export function initRatingScales(evaluation: EvaluationFragment) {
  return evaluation.categories.map((category) =>
    category.questions.map((question) => {
      if (isRatingEvaluationQuestion(question)) {
        return buildMultipleRatingScale(question.questionPossibleAnswers);
      }

      if (isBinaryEvaluationQuestion(question)) {
        return buildBinaryRatingScale(question.questionPossibleAnswers);
      }

      throw new Error("Unknown question type.");
    })
  );
}

export function extractNullQuestionIndexes(
  question: EvaluationQuestion,
  index: number
) {
  if (isBinaryEvaluationQuestion(question)) {
    return question.binaryAnswer === null ? [index] : [];
  }

  return question.ratingAnswer === null ? [index] : [];
}

export function extractCategoryAndQuestionIndexes(
  category: EvaluationCategory,
  index: number
): [number, number[]][] {
  const extractedQuestionIndexes = category.questions.flatMap(
    extractNullQuestionIndexes
  );

  if (!extractedQuestionIndexes.length) {
    // Filter out categories which has no null questions.
    return [];
  }

  return [[index, extractedQuestionIndexes]];
}

export function extractQuestions(nullAnswer: Unpacked<NullAnswers>) {
  const [, questions] = nullAnswer;

  return questions;
}

export function getHighestPoints(
  highest: number,
  answer: RatingAnswer | BinaryAnswer
) {
  return answer.points > highest ? answer.points : highest;
}

export function transformFormValueIntoInput(
  input: UpdateEvaluationOverride
): UpdateEvaluationMutationVariables {
  return {
    input: {
      ID: input.ID,
      feedback: input.feedback,
      revieweeUserId: input.revieweeUserId,
      categories: input.categories.map((category) => ({
        ID: category.ID,
        scoreCardCategoryId: category.scoreCardCategoryId,
        questions: category.questions.map((question) => ({
          ID: question.ID,
          scoreCardQuestionId: question.scoreCardQuestionId ?? "",
          feedback: question.feedback,
          ...(isRatingEvaluationQuestion(question) && {
            ratingAnswer: question.ratingAnswer,
          }),
          ...(isBinaryEvaluationQuestion(question) && {
            binaryAnswer: question.binaryAnswer,
          }),
        })),
      })),
    },
  };
}
